// @ts-nocheck

import React, { Fragment, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { ReactComponent as Arrow } from '../../../assets/svg/chevron-left.svg';
import BScrollView from '../../../components/BScrollView';
import { PANEL_PADDING } from '../constants/style';
import ImmersiveOutcomeWithVariationContainer from '../containers/ImmersiveOutcomeWithVariationContainer';

import ScrollingLabel from './ScrollingLabel';
import ScrollingLabelsProvider from './ScrollingLabelsProvider';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const ArrowIcon = styled(Arrow)`
  position: absolute;
  top: 1em;
  right: 1em;
  width: 1em;
  height: 1em;
  color: #fff;
  transform: ${({ $isRotated }) => ($isRotated ? 'rotateZ(-90deg)' : 'none')};
  transition: transform 300ms ease-out;
  z-index: 1;
`;

const ScrollableList = styled(BScrollView)`
  max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : '100%')};
  overflow-y: hidden;
  overflow-x: auto;
`;

const ScrollableSelect = styled(BScrollView)`
  max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : '100%')};
  overflow-y: hidden;
  overflow-x: auto;
`;

const BettypeSelect = styled.div`
  position: relative;
  color: #fff;
  border-radius: 2px;
  overflow: hidden;
`;

const BetType = styled.div`
  padding: 0.8em 0.5em;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.2;
  position: relative;
  color: #000;
  background-color: rgba(255, 255, 255, ${({ $pair }) => ($pair ? 0.8 : 0.7)});
`;

const SelectedBetType = styled(BetType)`
  padding: ${({ $hasMarginRight }) => (!$hasMarginRight ? '0.8em 0.5em' : '0.8em 2.5em 0.8em 0.5em')};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.2;
  position: relative;
  color: ${({ theme }) => theme.common.colors.secondary};
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.4);
`;

const SelectorLabel = styled(ScrollingLabel)`
  width: 100%;
`;

const Separator = styled.div`
  width: 100%;
  height: 0.4em;
`;

const ImmersiveOutcomesList = (props) => {
  const { onSelectChange, outcomes: outcomesGroup, bets, selectedBet, onClickOutcome, availableHeight } = props;
  const [isListOpened, setIsListOpened] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);
  const refScrollWrapper = useRef(null);

  const hasOnlyOneFacet = useMemo(() => bets.length === 1, [bets]);
  const canShowList = useMemo(() => selectedBet !== null, [selectedBet]);

  useEffect(() => {
    if (canShowList) {
      setMaxHeight(availableHeight - refScrollWrapper.current.getBoundingClientRect().top - PANEL_PADDING);
    }
  }, [availableHeight, canShowList]);

  useEffect(() => {
    if (selectedBet === null && bets.length > 0) {
      const firstBetGroup = bets[0].id;
      onSelectChange(firstBetGroup);
    }
  }, [bets, onSelectChange, selectedBet]);

  const handleClickSelector = useCallback(
    (groupId) => () => {
      if (!hasOnlyOneFacet) {
        if (groupId) {
          onSelectChange(groupId);
        }
        setIsListOpened((old) => !old);
      }
    },
    [hasOnlyOneFacet, onSelectChange],
  );

  const handleClickOutcome = useCallback(
    ({ oddId, betId, label, betTitle }) =>
      (value) => {
        onClickOutcome({ value, oddId, betId, outcomeLabel: label, betTitle });
      },
    [onClickOutcome],
  );

  if (!canShowList) {
    return null;
  }

  return (
    <Wrapper>
      <ScrollingLabelsProvider pauseDuration={2000}>
        <SelectedBetType onClick={handleClickSelector(null)} $hasMarginRight={!hasOnlyOneFacet}>
          <SelectorLabel key={selectedBet.label} label={selectedBet.label} />
          {!hasOnlyOneFacet ? <ArrowIcon $isRotated={isListOpened} /> : null}
        </SelectedBetType>
        <div ref={refScrollWrapper}>
          {!hasOnlyOneFacet ? (
            <BettypeSelect>
              {isListOpened ? (
                <ScrollableSelect $maxHeight={maxHeight}>
                  {bets
                    .filter((bet) => bet.id !== selectedBet.id)
                    .map((bet, index) => (
                      <BetType onClick={handleClickSelector(bet.id)} key={bet.label} $pair={index % 2}>
                        <SelectorLabel key={bet.label} label={bet.label} />
                      </BetType>
                    ))}
                </ScrollableSelect>
              ) : null}
            </BettypeSelect>
          ) : null}
          {!isListOpened ? (
            <ScrollableList $maxHeight={maxHeight}>
              {outcomesGroup.map((outcomes, index) => (
                <Fragment key={index}>
                  {outcomesGroup.length > 1 && index !== 0 ? <Separator /> : null}
                  {outcomes.map((outcome) => {
                    const { label, oddId, betId, mode } = outcome;
                    return (
                      <ImmersiveOutcomeWithVariationContainer
                        key={oddId}
                        oddId={oddId}
                        label={label}
                        handleClick={handleClickOutcome({ label, oddId, betId, betTitle: selectedBet.label })}
                        mode={mode}
                      />
                    );
                  })}
                </Fragment>
              ))}
            </ScrollableList>
          ) : null}
        </div>
      </ScrollingLabelsProvider>
    </Wrapper>
  );
};

export default ImmersiveOutcomesList;
